import { parseISO, format } from "date-fns";
import { nl } from "date-fns/locale";
import { Polygon } from "geojson";
import { AxiosRequestConfig } from "axios";

import { Api } from "./Api";

export const getLeadSearches = async (): Promise<ILeadSearch[]> => {
  /**
   * Retrieve lead-searches.
   */
  return Api.get("lead-searches/")
    .then((result: IApiSuccess) => {
      const leadSearchesData = result.data as IAxiosResponseData[];
      return leadSearchesData.map((leadSearchData) =>
        parseLeadSearchData(leadSearchData),
      );
    })
    .catch((error: IApiError) => Promise.reject(error));
};

export const getLeadSearch = async (
  leadSearchId: string,
): Promise<ILeadSearch> => {
  /**
   * Retrieve a lead-search by ID.
   */
  return Api.get(`lead-searches/${leadSearchId}/`)
    .then((result: IApiSuccess) => {
      const leadSearchData = result.data as IAxiosResponseData;
      return parseLeadSearchData(leadSearchData);
    })
    .catch((error: IApiError) => Promise.reject(error));
};

export const createLeadSearch = async (
  title: string,
  focusAreaPolygon: Polygon,
): Promise<ILeadSearch> => {
  /**
   * Create a lead-search.
   */
  if (focusAreaPolygon.coordinates[0].length < 4) {
    // The polygon must have at least 4 points (to form a closed shape).
    throw new Error("Invalid focus area polygon.");
  }
  const formData = {
    title: title,
    focus_area_polygon: focusAreaPolygon,
  };
  return Api.post("/lead-searches/", formData)
    .then((response: IApiSuccess) => {
      const leadSearchData = response.data as IAxiosResponseData;
      return parseLeadSearchData(leadSearchData);
    })
    .catch((error: IApiError) => Promise.reject(error));
};

export const deleteLeadSearch = async (leadSearchId: string): Promise<void> => {
  /**
   * Delete a lead-search by ID.
   */
  return Api.delete(`lead-searches/${leadSearchId}/`)
    .then(() => Promise.resolve())
    .catch((error: IApiError) => Promise.reject(error));
};

export const generateExcelForSelection = async (
  leadSearch: ILeadSearch,
  bagObjects: IBagObject[],
): Promise<void> => {
  /**
   * Generate an Excel file for selected BAG objects from a lead search.
   * This will trigger a download of the file.
   */
  const path = `lead-searches/${leadSearch.id}/generate-excel-for-selected-bag-objects/`;
  const formData = {
    bag_ids: bagObjects.map((bagObject) => bagObject.id),
  };
  const config: AxiosRequestConfig = { responseType: "blob" };
  await Api.post(path, formData, config).then((response: IApiSuccess) => {
    const link = document.createElement("a");
    link.href = URL.createObjectURL(response.data as Blob);
    const leadSearchTitleSlug = encodeURIComponent(
      leadSearch.title.replace(/\s+/g, "-").toLowerCase(),
    );
    link.download = `zoekopdracht-${leadSearchTitleSlug}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  });
};

export const parseLeadSearchData = (
  leadSearchData: IAxiosResponseData,
): ILeadSearch => {
  /**
   * Parse lead-search data from the API response, to a ILeadSearch object.
   */
  const date: Date = parseISO(leadSearchData.created as string);
  const focusAreaPolygon: Polygon = leadSearchData.focus_area_polygon;
  if (focusAreaPolygon.coordinates[0].length < 4) {
    // The polygon must have at least 4 points (to form a closed shape).
    throw new Error("Invalid focus area polygon.");
  }
  return {
    id: leadSearchData.id,
    title: leadSearchData.title,
    focusAreaPolygon: focusAreaPolygon,
    date: date,
    dateFormatted: format(date, "d MMMM yyyy", { locale: nl }),
  } as ILeadSearch;
};
